import { Component, OnDestroy, OnInit, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IntegratedPlatform,
  IntegrationModel,
  IntegrationPlatformImport,
  IntegrationPlatformType
} from 'app/integrations/models/integration.model';
import { IntegrationsService } from 'app/integrations/integration.service';
import { IntegrationPlatformSpecificsFactory } from 'app/integrations/integration-platform-specifics.factory';
import { NotificationService } from 'app/shared/notification.service';
import { AmplitudeService } from 'app/shared/services/amplitude.service';
import { Observable } from 'rxjs';
import { CrazyCallConfigService } from 'app/lead-generation/shared/services/crazycall-config.service';
import isInsideIframe from '../../shared/utils/isInsideIframe';
import { IntegrationStatePollingService } from '../integration-state-polling.service';
import { Ng1LiveChatService } from '../../shared/ng1/ng1-live-chat.service';
import { PlatformSpecifics } from '../integration-platform-specifics';
import { reloadCurrentRoute } from '../../shared/utils/navigate-force-reload';

@Component({
  selector: 'cc-single-integration',
  templateUrl: './single-integration.component.html',
  styleUrls: ['./single-integration.component.scss']
})
export class SingleIntegrationComponent implements OnInit, OnDestroy {
  @ViewChild('zapierScript') zapierScript: ElementRef;
  public integratedPlatformType: IntegrationPlatformType;
  public integrationPlatform: IntegratedPlatform;
  public platformString: string;
  public integrationProperties: PlatformSpecifics;

  public apiKey$: Observable<{ apiKey: string }>;
  public latestImport$: Observable<IntegrationPlatformImport>;

  public integrationInstalled: boolean;
  public uninstallEnabled: boolean;

  public uninstallInProgress = false;
  public duringResync = false;

  public accountName: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private integrationsService: IntegrationsService,
    private specificsFactory: IntegrationPlatformSpecificsFactory,
    private notificationService: NotificationService,
    private amplitudeService: AmplitudeService,
    private crazyCallConfigService: CrazyCallConfigService,
    private integrationStatePollingService: IntegrationStatePollingService,
    public ng1LiveChatService: Ng1LiveChatService
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.extractDataFromRouteResolver();
      this.logAmplitude('Init');

      this.latestImport$ = this.integrationsService.getLatestImport(this.integratedPlatformType);
      this.apiKey$ = this.integrationsService.getApiKey();

      this.accountName = this.crazyCallConfigService.account;

      this.startPollingForChangedState();
    });
  }

  ngOnDestroy(): void {
    this.integrationStatePollingService.stopPolling();
  }

  install() {
    this.logAmplitude('Install');
    if (this.integrationProperties.requiresSupportContact) {
      this.ng1LiveChatService.contactSupport();
    } else {
      window.open(this.integrationProperties.installationUrl, '_blank');
    }
  }

  resync() {
    this.logAmplitude('Resync');
    this.duringResync = true;
    this.integrationsService.importOldContacts(this.integratedPlatformType, true).subscribe();
  }

  uninstall() {
    this.logAmplitude('Uninstall');
    this.uninstallInProgress = true;
    this.specificsFactory
      .getSpecifics(this.integratedPlatformType)
      .handleUninstall(this.integrationPlatform)
      .subscribe(() => {
        this.notificationService.open('INTEGRATIONS.UNINSTALL_SUCCESS');
        this.router.navigateByUrl('/integrations/list');
      });
  }

  goBack() {
    this.logAmplitude('Going back');
    this.router.navigateByUrl('/integrations');
  }

  toggleAutoResync(enabled: boolean) {
    this.logAmplitude(`Toggle auto resync to ${enabled}`);
    this.integrationsService.toggleAutoResync(this.integratedPlatformType, enabled).subscribe();
  }

  openExternalPanel() {
    window.open(this.integrationProperties.externalPanelUrl, '_blank');
  }

  private extractDataFromRouteResolver() {
    const { platform, type } = this.route.snapshot.data['integration'];
    this.integratedPlatformType = type;
    this.integrationPlatform = platform;
    this.integrationInstalled = !!this.integrationPlatform;
    this.integrationProperties = this.specificsFactory.getSpecifics(this.integratedPlatformType);
    this.platformString = this.integratedPlatformType.toString().toLocaleLowerCase();
    this.uninstallEnabled = this.checkIfUninstallIsEnabled();
  }

  private checkIfUninstallIsEnabled() {
    return !isInsideIframe() && this.integrationProperties.allowsUninstall;
  }

  private startPollingForChangedState() {
    this.integrationStatePollingService
      .pollIntegrationState(this.integratedPlatformType)
      .subscribe((status: IntegrationModel) => {
        if (status.integrated !== this.integrationInstalled) {
          reloadCurrentRoute(this.router);
        }
      });
  }

  private logAmplitude(event: string) {
    this.amplitudeService.logEvent(`Panel:Integrations:Single: ${event}`, { platform: this.integratedPlatformType });
  }
}
